import React, { useEffect, useState } from "react";
import connectButtonSrc from "../assets/strava/btn_strava_connect_with_orange.svg";
import poweredByOrangeSrc from "../assets/strava/api_logo_pwrdBy_strava_stack_orange.svg";
import poweredByWhiteSrc from "../assets/strava/api_logo_pwrdBy_strava_stack_white.svg";

// Official Strava brand assets (developers.strava.com/guidelines). All must
// be used exactly as supplied — never redrawn, recoloured, or relabelled.
// Scaling is allowed; native sizes are 237x48 (button) and 176x60 (stacked
// "Powered by Strava"). The kit ships orange (light surfaces) and white (dark
// surfaces) variants; we pick by the active site theme.

const STACK_RATIO = 176 / 60;

function readIsLight(): boolean {
  if (typeof document === "undefined") return false;
  return document.documentElement.dataset["theme"] === "light";
}

/** True when the site is on the light theme (data-theme="light" on <html>).
 * Every other free theme and store skin is a dark surface. */
export function useIsLightTheme(): boolean {
  const [light, setLight] = useState(readIsLight);
  useEffect(() => {
    const el = document.documentElement;
    const obs = new MutationObserver(() => setLight(readIsLight()));
    obs.observe(el, { attributes: true, attributeFilter: ["data-theme"] });
    setLight(readIsLight());
    return () => obs.disconnect();
  }, []);
  return light;
}

// The button asset's own orange. A fullWidth row sits the untouched image on
// a field of exactly this colour so it lines up with the Apple / Google rows
// without scaling or restyling the asset itself.
const STRAVA_ORANGE = "#FC5200";

export function ConnectWithStravaButton({
  href,
  height = 48,
  fullWidth = false,
}: {
  href: string;
  height?: number;
  /** Stretch to the container's width on a matching orange field (login/signup rows). */
  fullWidth?: boolean;
}) {
  return (
    <a
      href={href}
      style={
        fullWidth
          ? {
              display: "flex",
              width: "100%",
              height,
              alignItems: "center",
              justifyContent: "center",
              background: STRAVA_ORANGE,
              borderRadius: "var(--radius-md)",
              lineHeight: 0,
            }
          : { display: "inline-flex", lineHeight: 0 }
      }
    >
      <img
        src={connectButtonSrc}
        alt="Connect with Strava"
        style={{ height, width: "auto", display: "block" }}
      />
    </a>
  );
}

export function PoweredByStrava({
  height = 32,
  style,
}: {
  height?: number;
  style?: React.CSSProperties;
}) {
  const light = useIsLightTheme();
  return (
    <img
      src={light ? poweredByOrangeSrc : poweredByWhiteSrc}
      alt="Powered by Strava"
      style={{ height, width: height * STACK_RATIO, display: "block", ...style }}
    />
  );
}

export function stravaActivityUrl(sourceId: string): string {
  return `https://www.strava.com/activities/${sourceId}`;
}

/** Where an athlete reviews or revokes Atlas's access on Strava's side. */
export const STRAVA_MANAGE_APPS_URL = "https://www.strava.com/settings/apps";

// Strava API Policy 2.1: before we access any Strava data the athlete must be
// told what is collected, how, how to withdraw consent, and how to have it
// deleted. Shown under every Connect with Strava button (web + mobile).
export function StravaConsentNote({
  align = "center",
  style,
  compact = false,
}: {
  align?: "center" | "left";
  style?: React.CSSProperties;
  /** One line + guide link for the login/signup pages (ATL-130); the full
   *  paragraph stays on Connected sources where the connect happens. */
  compact?: boolean;
}) {
  const linkStyle: React.CSSProperties = { color: "var(--text-accent)", textDecoration: "none" };
  if (compact) {
    return (
      <p
        style={{
          margin: 0,
          fontSize: 12,
          lineHeight: 1.5,
          color: "var(--text-muted)",
          textAlign: align,
          maxWidth: 420,
          ...style,
        }}
      >
        Strava: read-only, including private activities. Disconnect any time from Connected
        sources.{" "}
        <a href="/guides/what-happens-when-you-connect-strava" style={linkStyle}>
          What Atlas reads
        </a>
      </p>
    );
  }
  return (
    <p
      style={{
        margin: 0,
        fontSize: 12,
        lineHeight: 1.5,
        color: "var(--text-muted)",
        textAlign: align,
        maxWidth: 420,
        ...style,
      }}
    >
      Atlas reads your activities and their GPS tracks from Strava (read-only, including
      private activities) and uses them only to build your own Atlas record. Disconnect any
      time from Connected sources or{" "}
      <a href={STRAVA_MANAGE_APPS_URL} target="_blank" rel="noreferrer" style={linkStyle}>
        strava.com/settings/apps
      </a>
      ; disconnecting deletes the activities we imported.{" "}
      <a href="/guides/what-happens-when-you-connect-strava" style={linkStyle}>
        What happens when you connect
      </a>
      .
    </p>
  );
}
